import React from "react"
import { IconButton, BookmarkIcon, Tooltip, AddBookmarkIcon } from "@carrotfertility/carotene-core"
import {
  useAddMemberBookmarkedContent,
  useIsMemberBookmarkedContent,
  useRemoveMemberBookmarkedContent
} from "./hooks/useMemberBookmarkedContent"
import { useIntl } from "react-intl"
import { reportError } from "#/utils/ErrorReporting"

interface FavoriteButtonProps {
  contentfulEntryId: string
  categoryEntryId: string
  contentType: string
}

export function FavoriteButton({ contentfulEntryId, categoryEntryId, contentType }: FavoriteButtonProps): JSX.Element {
  const intl = useIntl()
  const { isFavorited, isLoading } = useIsMemberBookmarkedContent(contentfulEntryId, categoryEntryId)
  const addFavoriteMutation = useAddMemberBookmarkedContent()
  const removeFavoriteMutation = useRemoveMemberBookmarkedContent()

  const handleToggleFavorite = async () => {
    try {
      if (isFavorited) {
        await removeFavoriteMutation.mutateAsync({ contentfulEntryId, categoryEntryId })
      } else {
        await addFavoriteMutation.mutateAsync({ contentfulEntryId, categoryEntryId, contentType })
      }
    } catch (error) {
      reportError(error)
    }
  }

  const isOperationLoading = addFavoriteMutation.isLoading || removeFavoriteMutation.isLoading

  const ariaLabel = isFavorited
    ? intl.formatMessage({ defaultMessage: "Remove bookmark" })
    : intl.formatMessage({ defaultMessage: "Bookmark this content" })

  const tooltipText = isFavorited
    ? intl.formatMessage({ defaultMessage: "Saved" })
    : intl.formatMessage({ defaultMessage: "Save for later" })

  return (
    <Tooltip
      placement="bottom"
      title={tooltipText}
      enterDelay={500}
      leaveDelay={200}
      disableHoverListener={isLoading || isOperationLoading}
    >
      <IconButton
        onClick={handleToggleFavorite}
        disabled={isLoading || isOperationLoading}
        size="small"
        aria-label={ariaLabel}
        sx={(theme) => ({
          padding: theme.tokens.spacing.xs
        })}
      >
        {isFavorited ? <BookmarkIcon /> : <AddBookmarkIcon />}
      </IconButton>
    </Tooltip>
  )
}
